import React from 'react';
import axios from 'axios';
import { useHistory, useParams } from 'react-router-dom';
import Markdown from 'react-markdown';
import ld from 'lodash';

import { Posts } from '../../server/src/db';

import { cardImages } from '../itcgCardImages';
import { Deck } from '../game';
import { CardClasses, NonCharacter } from '../card';
import fatBack from '../images/fat-corner-up-left.svg';

import { parseDeckListIntoTable } from './deck';
import { Categories } from './writePosts';
import { parseUpdateBody } from './frontPost';

const baseStyle: React.CSSProperties = {
  margin: '1%',
  display: 'flex',
};

const postStyle: React.CSSProperties = {
  background: 'linear-gradient(176deg, #DED4B3, 3%, #F5F2E8, 94%, #DED4B3)',
};

const centerStyle: React.CSSProperties = {
  textAlign: 'center',
};

interface PostParams {
  idcat: string;
}

interface PostProp {
  server: string;
  username?: string;
}

interface ViewState {
  post: Posts;
  deckList?: Deck;
}

interface PostsState {
  posts: Posts[];
}

export function ITCGPosts(props: PostProp) {
  const { idcat } = useParams<PostParams>();

  const [post, setPost] = React.useState<ViewState>();
  const [posts, setPosts] = React.useState<PostsState>();
  const [onlyOwn, setOnlyOwn] = React.useState(false);
  const [excludeOwn, setExcludeOwn] = React.useState(false);
  const [bowmanFilter, setBowmanFilter] = React.useState(true);
  const [magicianFilter, setMagicianFilter] = React.useState(true);
  const [thiefFilter, setThiefFilter] = React.useState(true);
  const [warriorFilter, setWarriorFilter] = React.useState(true);
  const [charFilter, setCharFilter] = React.useState('');
  const [order, setOrder] = React.useState(true);

  const history = useHistory();

  const isCat = Object.keys(Categories).includes(idcat.toUpperCase());
  const [category, setCategory] = React.useState(
    post?.post.category
      ? post.post.category
      : isCat
        ? Categories[idcat.toUpperCase()]
        : undefined,
  );
  const [id, setId] = React.useState(!isCat ? idcat : undefined);

  React.useEffect(() => {
    (async () => {
      if (!id) return;

      if (posts) {
        const thisPost = posts.posts.find((post) => post.id === id);

        if (thisPost) {
          return setPost({
            post: thisPost,
            deckList: thisPost.postdecklist?.deck_list,
          });
        }
      }

      const resp = await axios(`/posts/${id}`, {
        baseURL: props.server,
        timeout: 5000,
        withCredentials: true,
      });

      if (!resp.data) return;
      setPost({
        post: resp.data.post,
        deckList: resp.data.post.postdecklist?.deck_list,
      });
    })();
  }, [id]);

  React.useEffect(() => {
    (async () => {
      if (!category || (posts?.posts.length && posts.posts.length > 0)) return;

      const resp = await axios(`/posts/category/${category}`, {
        baseURL: props.server,
        timeout: 5000,
        withCredentials: true,
      });

      if (!resp.data) return;
      setPosts(resp.data);
    })();
  }, [category]);

  const viewPosts = (props: PostProp, category: Categories) => {
    const cat = Categories[category.toUpperCase()];

    return (
      <div className="flex row" style={baseStyle}>
        <div className="border border-3 lg-12 col" style={postStyle}>
          <a className="paper-btn padding-none" href={'/posts'}>
            <img
              src={fatBack}
              alt={'back arrow'}
              className="no-border"
              style={{ width: '30px' }}
            />
          </a>
          <h1 style={centerStyle}>{categoryName(cat)}</h1>
          <div className="row">
            {cat === Categories.LIST && (
              <div className="collapsible col lg-12">
                <input id="collapsible2" type="checkbox" name="collapsible" />
                <label htmlFor="collapsible2">Filter</label>
                <div className="collapsible-body flex-spaces row">
                  {!!props.username && (
                    <div className="form-group margin-right">
                      <legend>Your Posts</legend>
                      <div className="border border-2 padding">
                        <label htmlFor="paperChecks1" className="paper-check">
                          <input
                            type="checkbox"
                            id="paperChecks1"
                            checked={onlyOwn}
                            onChange={(e) => setOnlyOwn(e.target.checked)}
                          />
                          <span>Only</span>
                        </label>
                        <label htmlFor="paperChecks2" className="paper-check">
                          <input
                            type="checkbox"
                            id="paperChecks2"
                            checked={excludeOwn}
                            onChange={(e) => setExcludeOwn(e.target.checked)}
                          />
                          <span>Exclude</span>
                        </label>
                      </div>
                    </div>
                  )}
                  <div className="form-group margin-right">
                    <legend>Deck Character</legend>
                    <div className="border border-2 padding flex row">
                      <label
                        htmlFor="paperSelects1"
                        className="col padding-none margin-right paper-check"
                      >
                        <select
                          id="paperSelects1"
                          value={charFilter}
                          onChange={(e) => setCharFilter(e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="ivan">Ivan</option>
                          <option value="maya">Maya</option>
                          <option value="mistmoon">Mistmoon</option>
                          <option value="nixie">Nixie</option>
                          <option value="sherman">Sherman</option>
                          <option value="starblade">Starblade</option>
                          <option value="stormwind">Stormwind</option>
                          <option value="skyhawk">Skyhawk</option>
                        </select>
                      </label>
                    </div>
                  </div>
                  <div className="form-group margin-right">
                    <legend>Deck Classes</legend>
                    <div className="border padding row">
                      <label
                        htmlFor="paperChecks2"
                        className="col sm-4 padding-none margin-right paper-check"
                      >
                        <input
                          type="checkbox"
                          id="paperChecks2"
                          checked={bowmanFilter}
                          onChange={(e) => setBowmanFilter(e.target.checked)}
                        />
                        <span>Bowman</span>
                      </label>
                      <label
                        htmlFor="paperChecks3"
                        className="col sm-4 padding-none margin-right paper-check"
                      >
                        <input
                          type="checkbox"
                          id="paperChecks3"
                          checked={magicianFilter}
                          onChange={(e) => setMagicianFilter(e.target.checked)}
                        />
                        <span>Magician</span>
                      </label>
                      <label
                        htmlFor="paperChecks4"
                        className="col sm-4 padding-none margin-right paper-check"
                      >
                        <input
                          type="checkbox"
                          id="paperChecks4"
                          checked={thiefFilter}
                          onChange={(e) => setThiefFilter(e.target.checked)}
                        />
                        <span>Thief</span>
                      </label>
                      <label
                        htmlFor="paperChecks5"
                        className="col sm-4 padding-none margin-right paper-check"
                      >
                        <input
                          type="checkbox"
                          id="paperChecks5"
                          checked={warriorFilter}
                          onChange={(e) => setWarriorFilter(e.target.checked)}
                        />
                        <span>Warrior</span>
                      </label>
                    </div>
                  </div>
                  <div className="form-group">
                    <legend>Order By</legend>
                    <div className="border padding-left padding-top">
                      <label
                        htmlFor="paperRadios1"
                        className="col padding-none margin-right paper-check"
                      >
                        <input
                          type="radio"
                          id="paperRadios1"
                          name="order"
                          checked={order}
                          onChange={(e) => setOrder(e.target.checked)}
                        />
                        <span>Created</span>
                      </label>
                      <label
                        htmlFor="paperRadios2"
                        className="col padding-none margin-right paper-check"
                      >
                        <input
                          type="radio"
                          id="paperRadios2"
                          name="order"
                          checked={!order}
                          onChange={(e) => setOrder(!e.target.checked)}
                        />
                        <span>Updated</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {posts?.posts
              .filter((post) => {
                if (cat !== Categories.LIST) return true;
                if (!post.postdecklist) return false;

                const classes = getClasses(post.postdecklist.deck_list.deck);
                return (
                  (!onlyOwn || post.owner.username === props.username) &&
                  (!excludeOwn || post.owner.username !== props.username) &&
                  (bowmanFilter || !classes.includes(CardClasses.Bowman)) &&
                  (magicianFilter || !classes.includes(CardClasses.Magician)) &&
                  (thiefFilter || !classes.includes(CardClasses.Thief)) &&
                  (warriorFilter || !classes.includes(CardClasses.Warrior)) &&
                  (charFilter === '' ||
                    post.postdecklist.deck_list.character.canonicalName === charFilter)
                );
              })
              .sort((a, b) => {
                if (!order)
                  return (
                    new Date(b.modified_at).getTime() - new Date(a.modified_at).getTime()
                  );

                return (
                  new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );
              })
              .map((post, index) => {
                const columnStyle = [Categories.LIST, Categories.UPDATE].includes(cat)
                  ? 'lg-6'
                  : 'lg-4';

                const colors = post.postdecklist
                  ? getColors(post.postdecklist.deck_list.deck)
                  : [];

                return (
                  <div className={columnStyle}>
                    <div
                      className="clickable"
                      onClick={() => {
                        setId(post.id);
                        history.push(`/posts/${post.id}`);
                      }}
                    >
                      <div
                        className={`margin-bottom padding-right padding-left border border-${(index % 6) + 1} shadow shadow-hover`}
                      >
                        {cat === Categories.LOG ? (
                          <h4 className="padding-left">
                            Log :: {new Date(post.created_at).toLocaleDateString()}
                          </h4>
                        ) : cat === Categories.UPDATE ? (
                          <>
                            <h2 className="col-9 padding-left margin-bottom">
                              {post.title}
                            </h2>
                            <h4 className="col-3">
                              {new Date(post.created_at).toLocaleDateString()}
                            </h4>
                            <p className="col-12 padding-right">
                              {parseUpdateBody(post.body)}
                            </p>
                          </>
                        ) : (
                          <div className="row">
                            <div className="col-8">
                              <h2 className="margin-bottom">{post.title}</h2>
                              <p className="margin-top">
                                by{' '}
                                <b style={{ fontSize: '150%' }}>{post.owner.username}</b>
                              </p>
                              <p>
                                Updated On:{' '}
                                <b>{new Date(post.modified_at).toLocaleDateString()}</b>
                              </p>
                              <h4
                                popover-left="Card Classes"
                                className="margin-top margin-bottom"
                              >
                                {colors.map((color) => {
                                  return <span className={`badge ${color}`}></span>;
                                })}
                              </h4>
                            </div>
                            <div className="col-4 padding-top">
                              <img
                                className="no-border"
                                src={
                                  cardImages[post.postdecklist!.deck_list.character.image]
                                    .top
                                }
                              ></img>
                              <img
                                className="no-border"
                                src={
                                  cardImages[post.postdecklist!.deck_list.character.image]
                                    .skill
                                }
                              ></img>
                              <img
                                className="no-border"
                                src={
                                  cardImages[post.postdecklist!.deck_list.character.image]
                                    .skill2
                                }
                              ></img>
                              <img
                                className="no-border"
                                src={
                                  cardImages[post.postdecklist!.deck_list.character.image]
                                    .skill3
                                }
                              ></img>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    );
  };

  const viewPost = (props: PostProp, id: string) => {
    const deletePost = async () => {
      const resp = await axios.post(
        '/posts/delete',
        {
          postId: id,
        },
        {
          baseURL: props.server,
          timeout: 5000,
          withCredentials: true,
        },
      );

      if (resp.data) return history.push('/posts');
    };

    const copyPost = async () => {
      const resp = await axios.post(
        '/posts/copy',
        {
          postId: id,
        },
        {
          baseURL: props.server,
          timeout: 5000,
          withCredentials: true,
        },
      );

      if (resp.data) return history.push('/decks');
    };

    return (
      <div className="flex row" style={baseStyle}>
        <div className="border border-3 sm-8 col" style={postStyle}>
          <div
            className="paper-btn padding-none"
            onClick={() => {
              if (!post) return history.push('/posts');

              setCategory(post.post.category);
              setId(undefined);
              history.push(`/posts/${post.post.category}`);
            }}
          >
            <img
              src={fatBack}
              alt={'back arrow'}
              className="no-border"
              style={{ width: '30px' }}
            />
          </div>
          {!!post?.deckList ? (
            <>
              <h1 className="margin-bottom" style={centerStyle}>
                {post.post.title}
              </h1>
              <p className="margin-top" style={centerStyle}>
                by <b style={{ fontSize: '170%' }}>{post.post.owner.username}</b>
              </p>
              <div className="row">
                <div className="sm-5 padding-top">
                  <img
                    className="no-border"
                    src={cardImages[post.deckList.character.image].top}
                  ></img>
                  <img
                    className="no-border"
                    src={cardImages[post.deckList.character.image].skill}
                  ></img>
                  <img
                    className="no-border"
                    src={cardImages[post.deckList.character.image].skill2}
                  ></img>
                  <img
                    className="no-border"
                    src={cardImages[post.deckList.character.image].skill3}
                  ></img>
                  <h4 popover-left="Card Classes" className="margin-left margin-top">
                    {getColors(post.deckList.deck).map((color) => {
                      return <span className={`badge ${color}`}></span>;
                    })}
                  </h4>
                </div>
                <div className="sm-7 col">{parseDeckListIntoTable(post.deckList)}</div>
              </div>
              <Markdown>{post.post.body}</Markdown>
            </>
          ) : !!post && post.post.category === Categories.LOG ? (
            <>
              <h1 style={centerStyle}>
                Log :: {new Date(post.post.created_at).toLocaleDateString()}
              </h1>
              <Markdown>{post.post.body}</Markdown>
            </>
          ) : !!post && post.post.category === Categories.UPDATE ? (
            <>
              <h1 className="margin-bottom" style={centerStyle}>
                {post.post.title}
              </h1>
              <Markdown>{post.post.body}</Markdown>
            </>
          ) : !!post ? (
            <>
              <h1 className="margin-bottom" style={centerStyle}>
                {post.post.title}
              </h1>
              <h3 className="margin-top" style={centerStyle}>
                by {post.post.owner.username}
              </h3>
              <Markdown>{post.post.body}</Markdown>
            </>
          ) : (
            <h1 style={centerStyle}>Post Not Found</h1>
          )}
        </div>
        <div className="sm-4 col">
          {post && (
            <div className="border border-2 padding">
              <p>
                <span className="badge secondary">Created</span>{' '}
                {new Date(post.post.created_at).toDateString()}
              </p>
              <p>
                <span className="badge success">Updated</span>{' '}
                {new Date(post.post.modified_at).toDateString()}
              </p>
            </div>
          )}
          {post && post.post.owner.username === props.username && (
            <div className="flex-spaces row margin-top padding">
              <a className="paper-btn btn-success" href={`/posts/write/${post.post.id}`}>
                Edit
              </a>
              <label className="paper-btn btn-danger-outline" htmlFor="modal-1">
                Delete
              </label>
              <input className="modal-state" id="modal-1" type="checkbox" />
              <div className="modal">
                <label className="modal-bg" htmlFor="modal-1"></label>
                <div className="modal-body">
                  <label className="btn-close" htmlFor="modal-1">
                    X
                  </label>
                  <h4 className="modal-title">Delete Post</h4>
                  <p className="modal-text">Are you sure you want to delete this post?</p>
                  <button onClick={() => deletePost()} className="paper-btn btn-danger">
                    Yes
                  </button>
                </div>
              </div>
            </div>
          )}
          {!props.username && post?.post.category === Categories.LIST && (
            <p className="border border-3 padding">
              Log in or Sign up to <b>copy this deck</b> list and play it yourself!
            </p>
          )}
          {post &&
            !!props.username &&
            post.post.owner.username !== props.username &&
            post.post.category === Categories.LIST && (
              <div className="flex-spaces row margin-top padding border border-4">
                <label className="paper-btn btn-secondary-outline" htmlFor="modal-1">
                  Copy
                </label>
                <input className="modal-state" id="modal-1" type="checkbox" />
                <div className="modal">
                  <label className="modal-bg" htmlFor="modal-1"></label>
                  <div className="modal-body">
                    <label className="btn-close" htmlFor="modal-1">
                      X
                    </label>
                    <h4 className="modal-title">Copy Deck</h4>
                    <p className="modal-text">
                      Create a copy of <strong>{post.post.title}</strong> in your decks?
                    </p>
                    <button
                      onClick={() => copyPost()}
                      className="paper-btn btn-secondary"
                    >
                      Yes
                    </button>
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>
    );
  };

  if (id) return viewPost(props, id);
  if (category) return viewPosts(props, category);
}

function categoryName(cat: Categories): string {
  switch (cat) {
    case Categories.LIST:
      return 'Deck Lists';
    case Categories.LOG:
      return 'Development Log';
    case Categories.UPDATE:
      return 'Game Updates';
    default:
      return 'default';
  }
}

function getClasses(
  decklist: [Omit<NonCharacter, 'key' | 'owner'>, number][],
): CardClasses[] {
  return ld(decklist)
    .map((card) => card[0].class)
    .uniq()
    .value();
}

function getColors(decklist: [Omit<NonCharacter, 'key' | 'owner'>, number][]): string[] {
  return ld(getClasses(decklist))
    .sort()
    .map((clas) => {
      switch (clas) {
        case CardClasses.Bowman:
          return 'success';
        case CardClasses.Magician:
          return 'danger';
        case CardClasses.Thief:
          return 'primary';
        case CardClasses.Warrior:
          return 'secondary';
      }
    })
    .value();
}
